// File: /opt/dev_environment/assets/workspace/app-init/src/pages/VokabelnPage/VokabelnPage.js

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useVocabulary } from '../../context/VocabularyContext';
import VocabularyForm from './VocabularyForm';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import './VokabelnPage.css';

function VokabelnPage() {
  const { cards, addCard, updateCard, deleteCard, isLoaded } = useVocabulary();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [tooltipCardId, setTooltipCardId] = useState(null);
  const [editCard, setEditCard] = useState(null);
  const [deleteCardData, setDeleteCardData] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const tooltipRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (searchParams.get('add') === 'true') {
      setShowAddDialog(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (tooltipCardId === null) return;

    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setTooltipCardId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [tooltipCardId]);

  const filteredCards = useMemo(() => {
    const sorted = [...cards].sort((a, b) =>
      (a.italianWord || '').localeCompare(b.italianWord || '', 'it')
    );

    if (!searchQuery.trim()) return sorted;

    const query = searchQuery.trim().toLowerCase();
    return sorted.filter(
      (card) =>
        (card.germanWord || '').toLowerCase().includes(query) ||
        (card.italianWord || '').toLowerCase().includes(query)
    );
  }, [cards, searchQuery]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setTooltipCardId(null);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleInfoClick = useCallback((cardId) => {
    setTooltipCardId((prev) => (prev === cardId ? null : cardId));
  }, []);

  const handleEditClick = useCallback((card) => {
    setTooltipCardId(null);
    setEditCard(card);
  }, []);

  const handleDeleteClick = useCallback((card) => {
    setTooltipCardId(null);
    setDeleteCardData(card);
  }, []);

  const handleAddSave = useCallback(async (formData) => {
    setIsSaving(true);
    try {
      await addCard({
        germanWord: formData.germanWord,
        italianWord: formData.italianWord,
        gender: formData.gender || null,
        category: formData.category || 'other',
        topicTag: formData.topicTag || null,
      });
      setShowAddDialog(false);
    } catch (error) {
      console.error('Failed to add card:', error);
    } finally {
      setIsSaving(false);
    }
  }, [addCard]);

  const handleEditSave = useCallback(async (formData) => {
    if (!editCard) return;
    setIsSaving(true);
    try {
      await updateCard(editCard.id, {
        germanWord: formData.germanWord,
        italianWord: formData.italianWord,
        gender: formData.gender || null,
        category: formData.category || 'other',
        topicTag: formData.topicTag || null,
      });
      setEditCard(null);
    } catch (error) {
      console.error('Failed to update card:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editCard, updateCard]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteCardData) return;
    try {
      await deleteCard(deleteCardData.id);
      setDeleteCardData(null);
    } catch (error) {
      console.error('Failed to delete card:', error);
    }
  }, [deleteCardData, deleteCard]);

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const hasCards = cards.length > 0;
  const hasSearchQuery = searchQuery.trim() !== '';
  const hasResults = filteredCards.length > 0;

  if (!isLoaded) {
    return (
      <div className="vokabeln-page">
        <div className="vokabeln-page__empty">
          <span className="vokabeln-page__empty-icon" role="img" aria-hidden="true">⏳</span>
          <h2 className="vokabeln-page__empty-title">Laden…</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="vokabeln-page">
      {!hasCards ? (
        <div className="vokabeln-page__empty">
          <span className="vokabeln-page__empty-icon" role="img" aria-hidden="true">
            📭
          </span>
          <h2 className="vokabeln-page__empty-title">Keine Vokabeln vorhanden</h2>
          <p className="vokabeln-page__empty-text">
            Füge dein erstes Wort hinzu!
          </p>
        </div>
      ) : (
        <>
          <div className="vokabeln-search">
            <input
              ref={searchInputRef}
              className="vokabeln-search__input"
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Italienisch oder Deutsch suchen…"
              aria-label="Vokabeln durchsuchen"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
            />
            {hasSearchQuery && (
              <button
                className="vokabeln-search__clear"
                type="button"
                onClick={handleClearSearch}
                aria-label="Suche zurücksetzen"
              >
                ✕
              </button>
            )}
            <span className="vokabeln-search__icon" aria-hidden="true">🔍</span>
          </div>

          {hasSearchQuery && (
            <div className="vokabeln-search__result-count" aria-live="polite">
              {filteredCards.length} von {cards.length} Vokabeln
            </div>
          )}

          {hasResults ? (
            <table className="vokabeln-table" role="table" aria-label="Vokabelliste">
              <thead>
                <tr>
                  <th scope="col">Info</th>
                  <th scope="col">Italienisch</th>
                  <th scope="col">Deutsch</th>
                  <th scope="col">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filteredCards.map((card) => (
                  <tr key={card.id}>
                    <td className="vokabeln-table__info-cell">
                      <button
                        className={`vokabeln-table__info-btn ${tooltipCardId === card.id ? 'vokabeln-table__info-btn--active' : ''}`}
                        type="button"
                        onClick={() => handleInfoClick(card.id)}
                        aria-label={`Info für ${card.italianWord}`}
                        aria-expanded={tooltipCardId === card.id}
                      >
                        ℹ
                      </button>
                      {tooltipCardId === card.id && (
                        <div
                          className="vokabeln-tooltip"
                          ref={tooltipRef}
                          role="tooltip"
                          aria-label={`Details für ${card.italianWord}`}
                        >
                          <div className="vokabeln-tooltip__row">
                            <span className="vokabeln-tooltip__label">Erstellt:</span>
                            <span className="vokabeln-tooltip__value">{formatDate(card.createdAt)}</span>
                          </div>
                          <div className="vokabeln-tooltip__row">
                            <span className="vokabeln-tooltip__label">Fach:</span>
                            <span className="vokabeln-tooltip__value">{card.compartment}</span>
                          </div>
                          {card.gender && (
                            <div className="vokabeln-tooltip__row">
                              <span className="vokabeln-tooltip__label">Geschlecht:</span>
                              <span className="vokabeln-tooltip__value">{card.gender}</span>
                            </div>
                          )}
                          {card.category && (
                            <div className="vokabeln-tooltip__row">
                              <span className="vokabeln-tooltip__label">Kategorie:</span>
                              <span className="vokabeln-tooltip__value">{card.category}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td>{card.italianWord}</td>
                    <td>{card.germanWord}</td>
                    <td className="vokabeln-table__action-cell">
                      <button
                        className="vokabeln-table__action-btn vokabeln-table__action-btn--edit"
                        type="button"
                        onClick={() => handleEditClick(card)}
                        aria-label={`${card.italianWord} bearbeiten`}
                      >
                        ✏️
                      </button>
                      <button
                        className="vokabeln-table__action-btn vokabeln-table__action-btn--delete"
                        type="button"
                        onClick={() => handleDeleteClick(card)}
                        aria-label={`${card.italianWord} löschen`}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="vokabeln-page__no-results">
              <span className="vokabeln-page__no-results-icon" role="img" aria-hidden="true">
                🔎
              </span>
              <p className="vokabeln-page__no-results-text">
                Keine Ergebnisse für{' '}
                <span className="vokabeln-page__no-results-query">„{searchQuery.trim()}"</span>
              </p>
            </div>
          )}
        </>
      )}

      <button
        className="vokabeln-fab"
        type="button"
        onClick={() => setShowAddDialog(true)}
        aria-label="Neue Vokabel hinzufügen"
      >
        <span className="vokabeln-fab__icon">+</span>
      </button>

      {showAddDialog && (
        <VocabularyForm
          title="Neue Vokabel hinzufügen"
          onSave={handleAddSave}
          onCancel={() => setShowAddDialog(false)}
        />
      )}

      {editCard && (
        <VocabularyForm
          title="Vokabel bearbeiten"
          initialData={{
            germanWord: editCard.germanWord,
            italianWord: editCard.italianWord,
            gender: editCard.gender || '',
            category: editCard.category || '',
            topicTag: editCard.topicTag || '',
          }}
          onSave={handleEditSave}
          onCancel={() => setEditCard(null)}
        />
      )}

      {deleteCardData && (
        <DeleteConfirmDialog
          card={deleteCardData}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteCardData(null)}
        />
      )}
    </div>
  );
}

export default VokabelnPage;